import React, { useMemo } from 'react';
import {
  SimpleForm,
  SaveButton,
  CreateBase,
} from 'react-admin';

import {
  Box,
  Typography,
  Grid,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

import cleaner from '../../utilities/dataProvider/cleaner';
import { ReportTypes, SelectableReportTypes } from '../../constants/reportTypes';
import reportCreateMap, { mapReportTransform } from './features/reportFeatures';
import { reportMetadata } from '../../constants/reportMetadata';
import ReportOutputConfiguration from './ReportOutputConfiguration';
import config from '../../config';

const transform = (data: {
  type: ReportTypes;
  outputConfig?: Record<string, {
    columns: Record<string, boolean>;
    rows: Record<string, boolean>;
  }>;
  report: unknown;
}) => {
  type SerializedSheetConfig = {
    columns: Record<string, 0 | 1>;
    rows: Record<string, 0 | 1>;
  };

  const toBinaryRecord = (source: Record<string, boolean> = {}): Record<string, 0 | 1> => (
    Object.fromEntries(
      Object.entries(source).map(([id, included]) => [id, included ? 1 : 0] as const),
    ) as Record<string, 0 | 1>
  );

  const getTransformedOutputConfig = (): Record<string, SerializedSheetConfig> | undefined => {
    if (!config.reportOutputConfigurationEnabled) {
      return undefined;
    }

    return Object.entries(data.outputConfig || {})
      .reduce<Record<string, SerializedSheetConfig>>((acc, [sheetId, sheetConfig]) => {
      const columns = toBinaryRecord(sheetConfig.columns);
      const rows = toBinaryRecord(sheetConfig.rows);

      return {
        ...acc,
        [sheetId]: {
          columns,
          rows,
        },
      };
    }, {});
  };

  const transformedOutputConfig = getTransformedOutputConfig();

  const cleanedData = cleaner({
    ...data,
    outputConfig: transformedOutputConfig,
  });

  const reportTransform = mapReportTransform[(cleanedData.type as ReportTypes)];
  if (!reportTransform) return cleanedData;
  return reportTransform(cleanedData);
};

const CustomToolbar = () => (
  <Box>
    <SaveButton
      label="Generate Report"
      icon={<SendIcon />}
      alwaysEnable
    />
  </Box>
);

const ReportCreate = ({
  reportType,
}: {
  reportType: SelectableReportTypes;
}) => {
  const FormComponent = reportCreateMap[reportType];
  const {
    detailedDescription,
    outputSheets,
  } = reportMetadata[reportType];

  const defaultValues = useMemo(() => ({
    type: reportType,
    outputConfig: config.reportOutputConfigurationEnabled ? outputSheets.reduce((acc, sheet) => {
      const columns = sheet.columns.reduce((colAcc, col) => ({
        ...colAcc,
        [col.id]: !col.defaultOff,
      }), {} as Record<string, boolean>);
      const rows = sheet.rows.reduce((rowAcc, row) => ({
        ...rowAcc,
        [row.id]: true,
      }), {} as Record<string, boolean>);

      return {
        ...acc,
        [sheet.id]: {
          columns,
          rows,
        },
      };
    }, {} as Record<string, {
      columns: Record<string, boolean>;
      rows: Record<string, boolean>;
    }>) : undefined,
  }), [reportType, outputSheets]);

  return (
    <CreateBase transform={transform} record={defaultValues}>
      <SimpleForm mode="all" toolbar={null} component={Box}>
        <Grid container spacing={8}>
          <Grid size={12}>
            <Typography variant="h4" color="primary" marginBottom={4}>
              About this report
            </Typography>
            <Box
              padding={3}
              sx={{
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {detailedDescription}
            </Box>
          </Grid>
          {FormComponent && (
            <Grid size={12} sx={{ maxWidth: 600 }}>
              <Typography variant="h4" color="primary" marginBottom={4}>
                Report parameters
              </Typography>
              <FormComponent />
            </Grid>
          )}
          {(outputSheets && outputSheets.length > 0 && config.reportOutputConfigurationEnabled) && (
            <Grid size={12}>
              <Typography variant="h4" color="primary" marginBottom={4}>
                Output configuration
              </Typography>
              <ReportOutputConfiguration outputSheets={outputSheets} />
            </Grid>
          )}
          <Grid size={12}>
            <CustomToolbar />
          </Grid>
        </Grid>
      </SimpleForm>
    </CreateBase>
  );
};

export default ReportCreate;
